import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signIn,
  signOut,
  signInWithRedirect,
} from "aws-amplify/auth";
import AuthLayout from "../../layouts/AuthLayout";
import ConsentBlurb from "../../components/auth/ConsentBlurb";
import { useRedirectIfAuthenticated } from "../../hooks/useRedirectIfAuthenticated";
import { DeviceAPI } from "../../services/device.service";
import { getDeviceId } from "../../utils/device-id";
import PasswordCriteria from "../../components/auth/PasswordCriteria";
import { meetsPasswordPolicy } from "../../utils/password-policy";
import { reportCognitoFailure } from "../../utils/flow-telemetry";
import { logger } from "../../utils/logger";
import {
  clearPendingVerification,
  readPendingVerification,
  writePendingVerification,
} from "../../utils/pending-verification";

type Step = "form" | "verify";


const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useRedirectIfAuthenticated();

  const [step, setStep] = useState<Step>("form");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [cognitoUsername, setCognitoUsername] = useState<string | null>(null);
  // Cognito only dispatches the code once per signUp call. Without a resend
  // path a user whose mail is slow or spam-filtered is stranded UNCONFIRMED
  // with no way forward: login throws UserNotConfirmedException and
  // ForgotPassword is refused while email_verified is false.
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSending, setResendSending] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const sendVerificationCode = async (username: string) => {
    setResendSending(true);
    setError(null);
    setInfo(null);
    try {
      await resendSignUpCode({ username });
      setResendCooldown(60);
      setVerificationCode("");
      setInfo("We have sent a new verification code to your email.");
    } catch (err: any) {
      logger.error(
        "Signup: resend verification code failed",
        err instanceof Error ? err : undefined,
        { errorName: err?.name }
      );
      setError(err?.message || "Could not resend the code. Please try again.");
    } finally {
      setResendSending(false);
    }
  };

  const handleResendCode = () => {
    if (!cognitoUsername) {
      setError("Missing username. Please start sign up again.");
      return;
    }
    void sendVerificationCode(cognitoUsername);
  };

  // Restore an in-flight verification on mount. Two entry points:
  //   - a refresh or reopened tab, restored from localStorage
  //   - LoginPage bouncing an UNCONFIRMED user here via router state, which
  //     also triggers a fresh code because their original one is long gone
  useEffect(() => {
    const bouncedEmail = (location.state as { verifyEmail?: string } | null)?.verifyEmail;
    if (bouncedEmail) {
      setEmail(bouncedEmail);
      setCognitoUsername(bouncedEmail);
      setStep("verify");
      writePendingVerification({ username: bouncedEmail, email: bouncedEmail });
      // Drop the router state so a later refresh restores from storage instead
      // of re-sending a code on every reload.
      navigate("/signup", { replace: true, state: null });
      void sendVerificationCode(bouncedEmail);
      return;
    }

    const pending = readPendingVerification();
    if (pending) {
      setEmail(pending.email);
      setCognitoUsername(pending.username);
      setStep("verify");
      setInfo("Enter the code we emailed you, or request a new one.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      try { await signOut(); } catch { /* ignore if not signed in */ }
      await signInWithRedirect({ provider: "Google" });
    } catch (err: any) {
      logger.error(
        "Signup: Google sign-in failed",
        err instanceof Error ? err : undefined,
        { errorName: err?.name }
      );
      setError(err?.message || "Could not start Google sign in. Please try again.");
    }
  };

  const handleSubmitSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const criteriaAllMet = meetsPasswordPolicy(password);

    if (!criteriaAllMet) {
      setError("Password must meet all requirements listed below.");
      return;
    }

    setIsSubmitting(true);
    const trimmedEmail = email.trim();
    try {
      const result = await signUp({
        username: trimmedEmail,
        password,
        options: {
          userAttributes: {
            email: trimmedEmail,
            given_name: firstName.trim(),
            family_name: lastName.trim(),
          },
        },
      });

      const username = result.userId ?? trimmedEmail;
      setCognitoUsername(username);
      writePendingVerification({ username, email: trimmedEmail });
      setStep("verify");
      setResendCooldown(60);
      setInfo("We have sent a verification code to your email.");
    } catch (err: any) {
      // UsernameExistsException covers two very different users and Cognito
      // will not say which: someone who signed up and never entered the code,
      // and someone who already has a working account. Asking for a resend
      // separates them. It succeeds only for an UNCONFIRMED user, so a success
      // means we can put them straight back on the code entry they lost, and
      // the failure means the account is live and they want to sign in.
      //
      // The previous copy told everyone their clinic admin had invited them,
      // which is wrong for a self-signup and offered no way out, so people
      // re-registered on a second email address instead.
      if (err?.name === "UsernameExistsException") {
        try {
          await resendSignUpCode({ username: trimmedEmail });
          setCognitoUsername(trimmedEmail);
          writePendingVerification({ username: trimmedEmail, email: trimmedEmail });
          setStep("verify");
          setResendCooldown(60);
          setInfo(
            "This email is already registered but not yet verified. We have sent you a new code."
          );
          // Resumed a stalled signup: expected path, nothing to report.
          return;
        } catch {
          // Report the signup error, not the resend one: the event that
          // matters is "signup hit an existing account" (a WARN, user state),
          // while the resend error for a live account is an expected refusal
          // that would otherwise count as a customer-impacting ERROR.
          reportCognitoFailure("COGNITO_SIGNUP_FAILED", err);
          // Two populations land here: someone whose own account is already
          // live, and clinic staff an admin invited, who sit in
          // FORCE_CHANGE_PASSWORD and also cannot be resent a signup code.
          // The copy has to serve both without guessing which one this is.
          setError(
            "An account already exists for this email. Please sign in instead. " +
              "If your clinic admin set this up for you, use the temporary password " +
              "from your invite email, or ask them to resend it. Otherwise use " +
              "Forgot password."
          );
          return;
        }
      }

      // One report per failure. logger.error would send a second, flow-less
      // report at ERROR even for user-input errors.
      reportCognitoFailure("COGNITO_SIGNUP_FAILED", err);
      setError(err?.message || "We could not create your account. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (!cognitoUsername) {
      setError("Missing username. Please start sign up again.");
      return;
    }

    setIsSubmitting(true);
    // Confirmation is the last step of sign-up; the auto sign-in after it is a
    // sign-in, and Pulse must not blame one for the other.
    let stage: "COGNITO_SIGNUP_FAILED" | "COGNITO_SIGNIN_FAILED" = "COGNITO_SIGNUP_FAILED";
    try {
      await confirmSignUp({
        username: cognitoUsername,
        confirmationCode: verificationCode.trim(),
      });
      clearPendingVerification();

      // A restored session has no password in memory, by design. The account
      // is confirmed at this point, so send them to login rather than failing
      // an auto-sign-in with an empty credential.
      if (!password) {
        navigate("/login", {
          replace: true,
          state: { info: "Email verified. Please sign in to continue." },
        });
        return;
      }

      // Sign in immediately after verification so the user lands in the
      // onboarding flow instead of being sent back to the login page.
      stage = "COGNITO_SIGNIN_FAILED";
      const { isSignedIn } = await signIn({ username: cognitoUsername, password });
      if (!isSignedIn) {
        // Unexpected extra step - fall back to login
        navigate("/login", { replace: true });
        return;
      }

      // Register this device before anything gated runs. A brand-new signup is
      // trusted here without a second code (backend check_device auto-trust).
      // Skipping it sent /auth/role's first call into a device 403, which
      // AuthInterceptor turned into a detour through /login (prod, 2026-11-02).
      // Any other answer is the login page's to handle: it shows the OTP step.
      const trusted = await DeviceAPI.check(getDeviceId()).then(
        (res) => res.trusted,
        () => false,
      );
      navigate(trusted ? "/auth/role" : "/login", { replace: true });
    } catch (err: any) {
      reportCognitoFailure(stage, err);
      const message =
        err?.message || "We could not verify the code. Please try again.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToLogin = () => {
    clearPendingVerification();
    navigate("/login");
  };

  if (step === "verify") {
    return (
      <AuthLayout
        title="Verify your email"
        subtitle="Enter the verification code sent to your email to activate your account."
        heroTitleLine1="Verify email"
        heroTitleLine2="Secure your Kaagazy account"
      >
        <button
          type="button"
          onClick={() => {
            clearPendingVerification();
            setStep("form");
            setError(null);
            setInfo(null);
          }}
          className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>
        <form onSubmit={handleSubmitVerify} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-600">
              Email
            </label>
            <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
              <input
                type="email"
                value={email}
                disabled
                className="w-full border-none bg-transparent text-base text-slate-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600">
              Verification code
            </label>
            <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
              <input
                type="text"
                required
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value)}
                className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                placeholder="Enter the 6 digit code"
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2.5">
              {error}
            </p>
          )}
          {info && (
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-2.5">
              {info}
            </p>
          )}

          <div className="mt-1 flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-36 inline-flex items-center justify-center min-h-[44px] rounded-full bg-[#0066ff] px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#0051cc] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Verifying..." : "Confirm"}
            </button>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendCooldown > 0 || resendSending || isSubmitting}
              className="inline-flex items-center justify-center min-h-[44px] rounded-full border border-[#0066ff] px-6 py-2.5 text-sm font-medium text-[#0066ff] hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : resendSending ? "Sending…" : "Resend code"}
            </button>
          </div>

          <div className="pt-4 text-sm text-slate-500">
            Already verified?
            <button
              type="button"
              onClick={goToLogin}
              className="ml-1 font-medium text-[#0066cc] hover:underline text-sm"
            >
              Go to login
            </button>
          </div>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign up"
      subtitle="Create your clinic account to get started."
      heroTitleLine1="Join us today"
      heroTitleLine2="Create your account"
    >
      <button
        type="button"
        onClick={goToLogin}
        className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to sign in
      </button>
      <form onSubmit={handleSubmitSignUp} className="space-y-3">
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-600">
              First name
            </label>
            <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
              <input
                type="text"
                required
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                placeholder="First name"
              />
            </div>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-600">
              Last name
            </label>
            <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
              <input
                type="text"
                required
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
                placeholder="Last name"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-600">
            Email
          </label>
          <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
              placeholder="Enter your email"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-600">
            Password
          </label>
          <div className="mt-1 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
            <input
              id="new-password"
              type={showPassword ? "text" : "password"}
              required
              name="new-password"
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
              placeholder="Create a password"
            />
            <button type="button" onClick={() => setShowPassword(v => !v)} className="ml-2 flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600" aria-label={showPassword ? "Hide password" : "Show password"}>
              {showPassword ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
            </button>
          </div>
          <PasswordCriteria password={password} />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-600">
            Confirm password
          </label>
          <div className="mt-1 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
            <input
              id="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              required
              name="confirm-password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className="w-full border-none bg-transparent text-base text-slate-900 outline-none placeholder-slate-400"
              placeholder="Re enter your password"
            />
            <button type="button" onClick={() => setShowConfirmPassword(v => !v)} className="ml-2 flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-600" aria-label={showConfirmPassword ? "Hide password" : "Show password"}>
              {showConfirmPassword ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
            </button>
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2.5">
            {error}
          </p>
        )}

        <ConsentBlurb prefix="By creating an account or continuing with Google, you agree to our" />

        <button
          type="submit"
          disabled={isSubmitting}
          className="mt-1 inline-flex items-center justify-center min-h-[44px] rounded-full bg-[#0066ff] px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#0051cc] disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {isSubmitting ? "Creating..." : "Create account"}
        </button>

        <div className="pt-2 space-y-2">
          <p className="text-sm text-slate-500">Or sign up with</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled
              title="Apple sign-in coming soon"
              className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm opacity-40 cursor-not-allowed"
              aria-label="Sign up with Apple (not available)"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6 fill-slate-900">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm hover:bg-slate-50 transition-colors"
              aria-label="Sign up with Google"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="pt-4 text-sm text-slate-500">
          Already have an account?{" "}
          <button
            type="button"
            onClick={goToLogin}
            className="font-medium text-[#0066cc] hover:underline text-sm"
          >
            Sign in
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default SignUpPage;
